import { Box, Button, HStack, Icon, Input, Tag, Text, Textarea } from "@chakra-ui/react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { FiCheck } from "react-icons/fi";
import { EspdResponseDataType } from "../../../../../autogen/bff-api";
import type { EspdRequirementResponseDto, ResolvedEspdRequirementDto } from "../../../../../autogen/bff-api";
import { answerHasValue } from "./espdCompleteness";

interface Props {
  requirement: ResolvedEspdRequirementDto;
  answer: EspdRequirementResponseDto | undefined;
  onChange: (patch: Partial<EspdRequirementResponseDto>) => void;
  readOnly: boolean;
}

const Labeled = ({
  label,
  labelId,
  optional = false,
  answered = false,
  children,
}: {
  label: string;
  labelId: string;
  optional?: boolean;
  answered?: boolean;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <Box>
      <HStack mb="2" spacing="2" align="center">
        <Text id={labelId} fontSize="sm" fontWeight="medium">
          {label}
        </Text>
        {optional ? (
          <Tag size="sm" variant="subtle" color="smMuted" bg="smBackgroundTertiary">
            {t("Optional")}
          </Tag>
        ) : (
          answered && <Icon as={FiCheck} boxSize="3.5" color="smPrimary" aria-hidden />
        )}
      </HStack>
      {children}
    </Box>
  );
};

const Indicator = ({
  labelledBy,
  value,
  onChange,
  isDisabled,
  yesLabel,
  noLabel,
}: {
  labelledBy: string;
  value: boolean | null | undefined;
  onChange: (yes: boolean) => void;
  isDisabled: boolean;
  yesLabel: string;
  noLabel: string;
}) => (
  <HStack spacing="2" role="group" aria-labelledby={labelledBy}>
    <Button
      size="sm"
      variant={value === true ? "solid" : "outline"}
      aria-pressed={value === true}
      colorScheme="teal"
      isDisabled={isDisabled}
      onClick={() => onChange(true)}
    >
      {yesLabel}
    </Button>
    <Button
      size="sm"
      variant={value === false ? "solid" : "outline"}
      aria-pressed={value === false}
      colorScheme="teal"
      isDisabled={isDisabled}
      onClick={() => onChange(false)}
    >
      {noLabel}
    </Button>
  </HStack>
);

export const EspdRequirementInput = ({
  requirement,
  answer,
  onChange,
  readOnly,
}: Props) => {
  const { t, i18n } = useTranslation();
  const label = (i18n.language.startsWith("no") && requirement.labelNorwegian) || requirement.label;
  const answered = answerHasValue(answer, requirement.responseDataType);
  const labelId = useId();
  const meta = { optional: requirement.optional, answered, labelId };

  switch (requirement.responseDataType) {
    case EspdResponseDataType.Indicator:
      return (
        <Box borderWidth="1px" borderRadius="md" p="4">
          <Labeled label={label} {...meta}>
            <Indicator
              labelledBy={labelId}
              value={answer?.indicator}
              onChange={(yes) => onChange({ indicator: yes })}
              isDisabled={readOnly}
              yesLabel={t("Yes")}
              noLabel={t("No")}
            />
          </Labeled>
        </Box>
      );

    case EspdResponseDataType.Description:
      return (
        <Labeled label={label} {...meta}>
          <Textarea
            aria-labelledby={labelId}
            isReadOnly={readOnly}
            value={answer?.description ?? ""}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </Labeled>
      );

    case EspdResponseDataType.Date:
      return (
        <Labeled label={label} {...meta}>
          <Input
            type="date"
            aria-labelledby={labelId}
            isReadOnly={readOnly}
            value={answer?.date ?? ""}
            onChange={(e) => onChange({ date: e.target.value })}
          />
        </Labeled>
      );

    case EspdResponseDataType.Period:
      return (
        <Labeled label={label} {...meta}>
          <Input
            aria-labelledby={labelId}
            isReadOnly={readOnly}
            value={answer?.period ?? ""}
            onChange={(e) => onChange({ period: e.target.value })}
          />
        </Labeled>
      );

    case EspdResponseDataType.EvidenceUrl:
      // The taxonomy defines a single URL requirement here (the sibling "Reference"/"Issuer"
      // requirements are separate CODE/DESCRIPTION fields); the generator only serialises the URL.
      return (
        <Labeled label={label} {...meta}>
          <Input
            type="url"
            aria-labelledby={labelId}
            isReadOnly={readOnly}
            value={answer?.evidenceUrl ?? ""}
            onChange={(e) => onChange({ evidenceUrl: e.target.value })}
          />
        </Labeled>
      );

    case EspdResponseDataType.Code:
    case EspdResponseDataType.CodeCountry:
      // CodeCountry uses a plain ISO-code text input for v1 (rare in-scope)
      return (
        <Labeled label={label} {...meta}>
          <Input
            aria-labelledby={labelId}
            isReadOnly={readOnly}
            value={answer?.code ?? ""}
            onChange={(e) => onChange({ code: e.target.value })}
          />
        </Labeled>
      );

    case EspdResponseDataType.Amount:
      return (
        <Labeled label={label} {...meta}>
          <HStack spacing="2">
            <Input
              type="number"
              aria-labelledby={labelId}
              isReadOnly={readOnly}
              value={answer?.amount ?? ""}
              onChange={(e) =>
                onChange({
                  amount: e.target.value === "" ? null : Number(e.target.value),
                  currencyId: answer?.currencyId ?? "NOK",
                })
              }
            />
            <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
              {answer?.currencyId ?? "NOK"}
            </Text>
          </HStack>
        </Labeled>
      );

    case EspdResponseDataType.Percentage:
      return (
        <Labeled label={label} {...meta}>
          <HStack spacing="2">
            <Input
              type="number"
              min={0}
              max={100}
              step="0.01"
              aria-labelledby={labelId}
              isReadOnly={readOnly}
              value={answer?.percentage ?? ""}
              onChange={(e) => onChange({ percentage: e.target.value === "" ? null : Number(e.target.value) })}
            />
            <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
              %
            </Text>
          </HStack>
        </Labeled>
      );

    case EspdResponseDataType.Quantity:
    case EspdResponseDataType.QuantityInteger:
    case EspdResponseDataType.QuantityYear:
      return (
        <Labeled label={label} {...meta}>
          <Input
            type="number"
            step={requirement.responseDataType === EspdResponseDataType.QuantityInteger ? 1 : undefined}
            aria-labelledby={labelId}
            isReadOnly={readOnly}
            value={answer?.quantity ?? ""}
            onChange={(e) => onChange({ quantity: e.target.value === "" ? null : Number(e.target.value) })}
          />
        </Labeled>
      );

    default:
      return (
        <Labeled label={label} {...meta}>
          <Input
            aria-labelledby={labelId}
            isReadOnly={readOnly}
            value={answer?.description ?? ""}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </Labeled>
      );
  }
};
